import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { TrendingUp, Users, Clock, DollarSign, Target } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard';
import OnboardingAnalyticsPanel from './components/OnboardingAnalyticsPanel';
import OffboardingInsightsPanel from './components/OffboardingInsightsPanel';
import BottleneckIdentificationPanel from './components/BottleneckIdentificationPanel';
import ComparativeAnalysisPanel from './components/ComparativeAnalysisPanel';
import PredictiveAnalyticsPanel from './components/PredictiveAnalyticsPanel';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const OnboardingOffboardingAnalyticsHub = () => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedView, setSelectedView] = useState('overview');
  const [dateRange, setDateRange] = useState('last_90_days');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const { companySettings } = useCompanySettings();

  const companyName = companySettings?.companyName || 'HR Analytics';
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit();

  useEffect(() => {
    document.title = `Onboarding & Offboarding Analytics - ${companyName}`;
  }, [companyName]);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const status = await billingService?.getSubscriptionStatus(user?.id);
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, selectedDepartment]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Simulated analytics data - replace with actual service calls
      const data = {
        executiveSummary: {
          avgOnboardingDuration: 14.5,
          onboardingCompletionRate: 92.3,
          costPerHire: 4850,
          exitProcessEfficiency: 87.6,
          activeOnboarding: 12,
          activeOffboarding: 3,
          timeToProductivity: 28.3,
          retentionRate: 94.2
        },
        onboardingMetrics: {
          totalOnboarded: 48,
          avgTimeToComplete: 14.5,
          taskCompletionRate: 92.3,
          newHireSatisfaction: 4.6,
          documentCollectionRate: 96.8,
          trainingCompletionRate: 89.4
        },
        offboardingMetrics: {
          totalExited: 8,
          avgExitProcessTime: 7.2,
          assetRecoveryRate: 98.5,
          knowledgeTransferScore: 85.3,
          exitInterviewCompletion: 87.5,
          complianceScore: 94.2
        },
        bottlenecks: [
          {
            id: 1,
            stage: 'Document Collection',
            type: 'onboarding',
            avgDelay: 3.2,
            impactedCases: 15,
            severity: 'medium',
            recommendation: 'Implement automated document reminders and digital upload portal'
          },
          {
            id: 2,
            stage: 'IT Equipment Setup',
            type: 'onboarding',
            avgDelay: 2.8,
            impactedCases: 12,
            severity: 'high',
            recommendation: 'Pre-provision equipment 5 days before start date'
          },
          {
            id: 3,
            stage: 'Exit Interview Scheduling',
            type: 'offboarding',
            avgDelay: 4.5,
            impactedCases: 5,
            severity: 'low',
            recommendation: 'Automate exit interview scheduling within 24 hours of resignation'
          }
        ],
        trends: [
          { month: 'Oct 2025', onboarding: 12, offboarding: 2, netChange: 10 },
          { month: 'Nov 2025', onboarding: 15, offboarding: 3, netChange: 12 },
          { month: 'Dec 2025', onboarding: 18, offboarding: 1, netChange: 17 },
          { month: 'Jan 2026', onboarding: 14, offboarding: 4, netChange: 10 }
        ],
        exitReasons: [
          { reason: 'Career Advancement', count: 3, percentage: 37.5 },
          { reason: 'Relocation', count: 2, percentage: 25.0 },
          { reason: 'Better Compensation', count: 2, percentage: 25.0 },
          { reason: 'Personal Reasons', count: 1, percentage: 12.5 }
        ],
        departmentPerformance: [
          { department: 'Finance', onboardingTime: 12.3, offboardingTime: 6.5, efficiency: 92.1 },
          { department: 'Operations', onboardingTime: 15.8, offboardingTime: 7.8, efficiency: 88.4 },
          { department: 'HR', onboardingTime: 13.2, offboardingTime: 6.2, efficiency: 94.3 },
          { department: 'IT', onboardingTime: 16.5, offboardingTime: 8.1, efficiency: 85.7 }
        ]
      };

      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalFilterChange = (filters) => {
    if (filters?.dateRange) setDateRange(filters?.dateRange);
    if (filters?.department) setSelectedDepartment(filters?.department);
  };

  const handleExport = () => {
    if (!isEditable) {
      startEdit('export');
      return;
    }
    console.log('Exporting analytics report...');
  };

  return (
    <>
      <Helmet>
        <title>Onboarding & Offboarding Analytics - {companyName}</title>
        <meta name="description" content="Comprehensive insights into employee lifecycle management with performance metrics and process optimization" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <LegalDisclaimerBanner />

        <main className="container mx-auto px-4 py-6 md:py-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Onboarding & Offboarding Analytics Hub
                </h1>
                <p className="text-muted-foreground">
                  Comprehensive insights into employee lifecycle management with performance metrics and optimization recommendations
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e?.target?.value)}
                  className="px-4 py-2 border border-border rounded-md bg-card text-foreground"
                >
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="last_90_days">Last 90 Days</option>
                  <option value="last_6_months">Last 6 Months</option>
                  <option value="last_year">Last Year</option>
                </select>
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth"
                >
                  Export Report
                </button>
              </div>
            </div>
          </div>

          {/* View Tabs */}
          <div className="mb-6 border-b border-border">
            <div className="flex gap-6 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'onboarding', label: 'Onboarding Analytics' },
                { id: 'offboarding', label: 'Offboarding Insights' },
                { id: 'bottlenecks', label: 'Bottleneck Analysis' },
                { id: 'comparative', label: 'Comparative Analysis' },
                { id: 'predictive', label: 'Predictive Analytics' }
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setSelectedView(tab?.id)}
                  className={`pb-3 px-2 font-medium transition-smooth whitespace-nowrap ${
                    selectedView === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Executive Summary Cards */}
              {selectedView === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    <ExecutiveSummaryCard
                      title="Avg Onboarding Duration"
                      value={`${analyticsData?.executiveSummary?.avgOnboardingDuration} days`}
                      icon="Clock"
                      trend={-8.2}
                      status="compliant"
                      subtitle="Target: 15 days"
                    />
                    <ExecutiveSummaryCard
                      title="Completion Rate"
                      value={`${analyticsData?.executiveSummary?.onboardingCompletionRate}%`}
                      icon="Target"
                      trend={3.5}
                      status="compliant"
                      subtitle="Industry avg: 85%"
                    />
                    <ExecutiveSummaryCard
                      title="Cost Per Hire"
                      value={`K${analyticsData?.executiveSummary?.costPerHire?.toLocaleString()}`}
                      icon="DollarSign"
                      trend={-5.3}
                      status="compliant"
                      subtitle="PNG Kina"
                    />
                    <ExecutiveSummaryCard
                      title="Exit Process Efficiency"
                      value={`${analyticsData?.executiveSummary?.exitProcessEfficiency}%`}
                      icon="TrendingUp"
                      trend={2.1}
                      status="compliant"
                      subtitle="Compliance score"
                    />
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-blue-500/10 rounded-md">
                          <Users className="w-5 h-5 text-blue-500" />
                        </div>
                        <span className="text-sm text-muted-foreground">Active Onboarding</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{analyticsData?.executiveSummary?.activeOnboarding}</p>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-orange-500/10 rounded-md">
                          <Users className="w-5 h-5 text-orange-500" />
                        </div>
                        <span className="text-sm text-muted-foreground">Active Offboarding</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{analyticsData?.executiveSummary?.activeOffboarding}</p>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-green-500/10 rounded-md">
                          <Clock className="w-5 h-5 text-green-500" />
                        </div>
                        <span className="text-sm text-muted-foreground">Time to Productivity</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{analyticsData?.executiveSummary?.timeToProductivity} days</p>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-purple-500/10 rounded-md">
                          <TrendingUp className="w-5 h-5 text-purple-500" />
                        </div>
                        <span className="text-sm text-muted-foreground">Retention Rate</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">{analyticsData?.executiveSummary?.retentionRate}%</p>
                    </div>
                  </div>

                  {/* Main Analytics Panels */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <OnboardingAnalyticsPanel data={analyticsData?.onboardingMetrics} trends={analyticsData?.trends} />
                    <OffboardingInsightsPanel data={analyticsData?.offboardingMetrics} exitReasons={analyticsData?.exitReasons} />
                  </div>
                </div>
              )}

              {selectedView === 'onboarding' && (
                <OnboardingAnalyticsPanel data={analyticsData?.onboardingMetrics} trends={analyticsData?.trends} detailed />
              )}

              {selectedView === 'offboarding' && (
                <OffboardingInsightsPanel data={analyticsData?.offboardingMetrics} exitReasons={analyticsData?.exitReasons} detailed />
              )}

              {selectedView === 'bottlenecks' && (
                <BottleneckIdentificationPanel bottlenecks={analyticsData?.bottlenecks} />
              )}

              {selectedView === 'comparative' && (
                <ComparativeAnalysisPanel departmentPerformance={analyticsData?.departmentPerformance} />
              )}

              {selectedView === 'predictive' && (
                <PredictiveAnalyticsPanel trends={analyticsData?.trends} />
              )}
            </>
          )}
        </main>

        {showRedirectModal && (
          <TeaserEditModal
            isOpen={showRedirectModal}
            onClose={closeModal}
            feature={teaserEditField}
            subscriptionStatus={subscriptionStatus}
          />
        )}
      </div>
    </>
  );
};

export default OnboardingOffboardingAnalyticsHub;